import { Injectable } from '@angular/core';
import { CookieService as AngularCookieService } from 'ngx-cookie-service';

/**
 * This enum defines the name of all events to be thrown
 * They have to be activated individually in the usercentrics backend
 */
enum UsercentricsEvent {
  // This event gets triggered whenever a Consent Status changed. The status of this event is telling if all consents were accepted or not.
  CONSENT_ALL_CHANGE = 'all_consents_given_status',
}

/**
 * This enum defines the custom data processing service names
 */
export enum DataProcessingServiceName {
  BANNER = 'Banner',
}

/**
 * This enum defines the cookies, associated to the service names
 */
export enum DataProcessingServiceCookie {
  BANNER = 'banner_',
}

declare const window: any;

/**
 * This interface defines the necessary attributes, that a consent object from usercentrics must have
 */
interface IConsent {
  dataProcessingService: string;
  consentStatus: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CookieService extends AngularCookieService {
  public async init(): Promise<void> {
    await this.usercentricsAsyncInit();
    this.registerListener();
  }

  protected async usercentricsAsyncInit(): Promise<void> {
    return new Promise((resolve, reject) => {
      window.usercentrics = window.usercentrics || {};
      try {
        if (window.usercentrics.isInitialized) {
          resolve();
        } else {
          window.usercentrics.onViewInit = (): void => {
            resolve();
          };
        }
      } catch (error) {
        reject(error as Error);
      }
    });
  }

  public registerListener(): void {
    // Name of event is editable as data layer in usercentrics backend.
    window.addEventListener('consent_status_change', this.consentStatusChangeHandler.bind(this));
  }

  public unregisterListener(): void {
    // Name of event is editable as data layer in usercentrics backend.
    window.removeEventListener('consent_status_change', this.consentStatusChangeHandler.bind(this));
  }

  protected consentStatusChangeHandler(e: any): void {
    switch (e.data.event) {
      case UsercentricsEvent.CONSENT_ALL_CHANGE:
        this.removeCookiesIfNecessary();
        break;
      default:
        break;
    }
  }

  public isCookieAllowed(name: DataProcessingServiceName): boolean {
    const assocService = this.getConsents().find((item) => item.dataProcessingService.includes(name));
    return typeof assocService !== 'undefined' && assocService.consentStatus;
  }

  public getConsents(): Array<IConsent> {
    return window.usercentrics.getConsents().filter(this.filterCustomServices);
  }

  protected filterCustomServices(item: IConsent): boolean {
    return Object.keys(DataProcessingServiceName).includes(item.dataProcessingService.toUpperCase());
  }

  protected removeCookiesIfNecessary(): void {
    for (const [key, name] of Object.entries(DataProcessingServiceName)) {
      if (this.isCookieAllowed(name)) {
        continue;
      }

      // Get name of cookie for current service name.
      // @ts-ignore
      const assocCookieName = DataProcessingServiceCookie[key] as string;

      // Find actual cookie.
      const existingCookie = Object.keys(this.getAll()).find((item: string) => item.includes(assocCookieName));

      if (typeof existingCookie === 'undefined') {
        continue;
      }

      // Delete cookie.
      this.delete(existingCookie, undefined);
    }
  }
}
